import { fetchEntitlements } from '@api-service/entitlements';
import type { MembershipEntitlements } from '@api-service/entitlements';
import { withAccessTokenLock } from '@lib/auth0/access-token-lock';
import { ACCESS_TOKEN_ROUTE } from '@lib/auth0/access-token-route';

export type EntitlementWaitOutcome =
  | 'entitlement-found'
  | 'exhausted'
  | 'unauthenticated';

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_DELAY_MS = 2500;
const HTTP_UNAUTHORIZED = 401;
const FREE_TIER = 'FREE';

type AccessTokenResult =
  | { status: 'ok'; token: string }
  | { status: 'unauthenticated' }
  | { status: 'unavailable' };

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const requestAccessToken = async (
  isForcedRefresh: boolean
): Promise<AccessTokenResult> => {
  try {
    const response = await withAccessTokenLock(() =>
      fetch(
        ACCESS_TOKEN_ROUTE,
        isForcedRefresh ? { method: 'POST' } : undefined
      )
    );

    if (response.status === HTTP_UNAUTHORIZED) {
      return { status: 'unauthenticated' };
    }
    if (!response.ok) return { status: 'unavailable' };

    const data = await response.json();
    return typeof data.token === 'string'
      ? { status: 'ok', token: data.token }
      : { status: 'unavailable' };
  } catch (error) {
    console.error('Access token request failed:', error);
    return { status: 'unavailable' };
  }
};

const readEntitlements = async (
  accessToken: string
): Promise<MembershipEntitlements | undefined> => {
  try {
    return await fetchEntitlements(accessToken);
  } catch (error) {
    console.error('Entitlements read failed:', error);
    return undefined;
  }
};

/**
 * Whether ffh-backend has caught up with the purchase.
 *
 * The tier is the only signal, because it is the one ffh-backend computes from
 * the subscription's current state. The subscription record itself is not:
 * a cancelled or expired one still appears there while the tier reads FREE, so
 * counting it would end this wait immediately for a returning member, before
 * the purchase they just made had landed.
 *
 * A degraded answer never counts either: it reports FREE because a lookup
 * failed, which is exactly the state this poll is waiting to come out of.
 */
export const isPaidEntitlement = (
  entitlements: MembershipEntitlements
): boolean =>
  !entitlements.isDegraded && entitlements.tier.toUpperCase() !== FREE_TIER;

/**
 * Waits for a completed checkout to reach the member's entitlements, then
 * stamps them into a freshly issued access token (FFH-3019).
 *
 * This replaces the loop that forced Auth0 refresh-token grants until the
 * post-login Action re-minted the subscription claim (FFH-1597). The wait it
 * covers is the same one: Stripe's webhook reaching the subscriptions service.
 * What changed is where the answer is read. `/users/me/entitlements` resolves
 * it live on every call, so the poll costs no token issuance, and one forced
 * refresh at the end mints the token the member carries into the hub.
 *
 * The retry is bounded so nobody is stranded here: callers should proceed on
 * 'exhausted' too, which is also where a rotation that never succeeds ends up.
 * A 401 reports 'unauthenticated' immediately, because there is no session to
 * read entitlements with.
 */
export const awaitPaidEntitlement = async ({
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  delayMs = DEFAULT_DELAY_MS
}: {
  maxAttempts?: number;
  delayMs?: number;
} = {}): Promise<EntitlementWaitOutcome> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const tokenResult = await requestAccessToken(false);

    if (tokenResult.status === 'unauthenticated') return 'unauthenticated';

    if (tokenResult.status === 'ok') {
      const entitlements = await readEntitlements(tokenResult.token);

      if (entitlements && isPaidEntitlement(entitlements)) {
        // The cached token still carries the tier the member arrived with, and
        // server-side gates read the token rather than this endpoint, so the
        // session is rotated here to pick the new roles up. The wait is only
        // over once that succeeds: reporting success on a failed rotation
        // would send a member who has just paid into the hub holding the tier
        // they arrived with, for as long as their cached token lives.
        const rotation = await requestAccessToken(true);
        if (rotation.status === 'ok') return 'entitlement-found';
        if (rotation.status === 'unauthenticated') return 'unauthenticated';
      }
    }

    if (attempt < maxAttempts) await wait(delayMs);
  }

  return 'exhausted';
};
